import { format } from 'date-fns';

import {
  AVAILABLE_PANELISTS_CAP,
  DEFAULT_RESPONSE_COUNT,
  QUESTION_TYPES,
  SURVEY_TYPES,
  VIDEO_SINGLE_GROUP_RESPONSE_COUNT,
} from '@api/instant_survey';
import { isEUDomain } from '@helpers';
import { formatData } from '@utilities';

import { SURVEY_TEMPORARY_TITLE } from './constants';
/**
 * format available panelists
 * @param  {Object} options.group
 * @param  {Number} options.group.quotaLimit
 * @param  {Number} options.group.validationUsers
 * @return {String}
 */
export const formatAvailablePanelists = ({ quotaLimit, validationUsers }) => {
  const cappedAvailablePanelists = Math.min(AVAILABLE_PANELISTS_CAP, 15 * quotaLimit);
  const displayedPanelistCount = Math.min(validationUsers, cappedAvailablePanelists);

  return `${formatData({ format: '0,0', value: displayedPanelistCount })}${
    validationUsers >= cappedAvailablePanelists ? '+' : ''
  }`;
};

/**
 * generate temporary survey title
 * @param  {Date} date
 * @return {String}
 */
export const generateTemporarySurveyTitle = (date = new Date()) =>
  `${SURVEY_TEMPORARY_TITLE} - ${format(date, 'MMM dd, yyyy')}`;

/**
 * get question cost
 * @param {Object} question
 * @param {Boolean} question._delete
 * @param {Object} question.properties
 * @param {String} question.type
 * @returns {Number} integer cost to compare against questionnaire pricing
 */
export const getQuestionCost = ({ _delete, properties, subQuestions, type }) => {
  if (_delete) return 0;

  switch (type) {
    case QUESTION_TYPES.INSTRUCTIONS.value:
      return 0;
    case QUESTION_TYPES['TABLE-RADIO'].value:
      return Math.ceil(subQuestions?.length / 4);
    case QUESTION_TYPES.MAX_DIFF.value:
      return Math.ceil(properties?.numberOfSets / 5);
    default:
      return 1;
  }
};

/**
 * get questions cost - reducer shortcut for get question cost
 * @param {Object[]} questions
 * @returns {Number}
 */
export const getQuestionsCost = (questions = []) =>
  questions.reduce((count, question) => count + getQuestionCost(question), 0);

/**
 * Count non-instruction questions before a given index
 * Used to determine if a question is beyond Q2 for disqualify logic rules
 * @param {Array} questions - Array of question objects
 * @param {Number} index - Index of the current question
 * @returns {Number} Count of non-instruction questions before this index
 */
export const getNonInstructionQuestionCountBefore = (questions, index) => {
  return questions.slice(0, index).filter(({ type }) => type !== QUESTION_TYPES.INSTRUCTIONS.value)
    .length;
};

/**
 * Calculate sequential starting question numbers for each question, used for numeric comparisons
 * (e.g. reorder logic, skip logic references). Returns raw integers, not display ranges.
 * Does not filter deleted questions — relies on getQuestionCost returning 0 for deleted entries.
 * For UI/DOCX display labels (e.g. "2-4"), use getQuestionDisplayIndexById.
 * @param  {Array} questionsList
 * @return {Object} mapping of question IDs to their starting question number
 */
export const calculateQuestionNumbers = (questionsList) => {
  return questionsList.reduce(
    (acc, question) => {
      const questionCost = getQuestionCost(question);
      acc.questionNumbersByQuestions[question.id] = acc.questionNumber;
      acc.questionNumber += questionCost;
      return acc;
    },
    {
      questionNumber: 1,
      questionNumbersByQuestions: {},
    }
  ).questionNumbersByQuestions;
};

/**
 * Get display index (number or range) for each question
 * @param {Array} questions
 * @returns {Object} mapping of question IDs to display index (e.g., '2-3')
 */
export const getQuestionDisplayIndexById = (questions) => {
  let currentIndex = 1;
  return questions
    .filter(({ _delete }) => !_delete)
    .reduce((acc, question) => {
      if (question.type === QUESTION_TYPES.INSTRUCTIONS.value) {
        acc[question.id] = 'T';
        return acc;
      }
      const questionCost = getQuestionCost(question);
      acc[question.id] =
        `${currentIndex}${questionCost > 1 ? `-${currentIndex + questionCost - 1}` : ''}`;
      currentIndex += questionCost;
      return acc;
    }, {});
};

/**
 * get Max Diff question information including cost, display counts, and formatted text
 * @param {Object} question
 * @param {Object} question.properties
 * @param {Number} question.properties.numberOfSets
 * @param {Number} question.properties.optionsPerSet
 * @param {Array} question.answers
 * @returns {Object} { questionCost, timesPerExperiment, numberOfSets, optionsPerSet, getText }
 */
export const getMaxDiffQuestionInfo = (question) => {
  const questionCost = getQuestionCost(question);
  const timesPerExperiment = question.answers?.length
    ? Math.ceil(
        (question.properties?.numberOfSets * question.properties?.optionsPerSet) /
          question.answers.length
      )
    : 0;

  return {
    getText: () =>
      `This will count as ${questionCost} question${questionCost !== 1 ? 's' : ''} for your survey. ` +
      `It will be displayed as ${question.properties?.numberOfSets} separate questions; each question will show ` +
      `${question.properties?.optionsPerSet} options; each option will show ${timesPerExperiment} time(s) in the experiment.`,
    numberOfSets: question.properties?.numberOfSets,
    optionsPerSet: question.properties?.optionsPerSet,
    questionCost,
    timesPerExperiment,
  };
};

/**
 * get numeric question type descriptive text based on properties
 * @param {Object} properties
 * @returns {String}
 */
export const getNumericTypeText = (properties) => {
  const min = properties?.minNumber ?? '';
  const max = properties?.maxNumber ?? '';
  if (properties?.forceCurrency) {
    return `currency write-in between $${min} and $${max}`;
  }
  if (properties?.forcePercent) {
    return `percentage write-in between ${min}% and ${max}%`;
  }
  return `numeric write-in between ${min} and ${max}`;
};

/**
 * get current questionnaire price
 * @param  {Object[]} options.questionnairePricing
 * @param  {Object} options.survey
 * @return {Object}
 */
export const getCurrentQuestionnairePrice = ({ questionnairePricing, survey }) => {
  const totalCount = getQuestionsCost(survey.questions);
  return questionnairePricing?.find((p) => p.questions === totalCount);
};

/**
 * get current sample price
 * @param  {Object[]} options.samplePricing
 * @param  {Object} options.survey
 * @return {Object}
 */
export const getCurrentSamplePrice = ({ samplePricing, survey }) => {
  const activeGroups = survey.groups.filter(({ _delete }) => !_delete);

  return samplePricing?.find(
    ({ quotaGroups, responses }) =>
      quotaGroups === activeGroups.length &&
      responses === activeGroups.reduce((acc, { quotaLimit }) => acc + quotaLimit, 0)
  );
};

/**
 * get quota limit
 * @param {String} surveyTypeId
 * @return {Number}
 */
export const getQuotaLimit = (surveyTypeId) => {
  if (surveyTypeId === SURVEY_TYPES.VIDEO) return VIDEO_SINGLE_GROUP_RESPONSE_COUNT;
  if (isEUDomain()) return DEFAULT_RESPONSE_COUNT;
  const surveyTypesWithHigherDefault = [
    'standard-v3-instant-survey-type',
    'monadic-concept-test-v3-instant-survey-type',
    'concept-test-v3-instant-survey-type',
    'turf-v3-instant-survey-type',
    'sequential-monadic-concept-test-v3-instant-survey-type',
    'new-item-v3-instant-survey-type',
  ];
  return surveyTypesWithHigherDefault.includes(surveyTypeId) ? 300 : DEFAULT_RESPONSE_COUNT;
};

/** get modal config based on survey type
 * @param {String} surveyType
 * @return {Object|null}
 */
export const getModalConfig = ({ surveyType }) => {
  switch (surveyType) {
    case 'monadic':
      return {
        description: `The Monadic Concept Test is used to evaluate multiple concepts—such as product designs,
          claims, or ads—by showing each respondent only one concept. This setup enables clean comparisons
          across distinct groups without bias from seeing multiple options.`,
        link: 'https://help.insights.numerator.com/en/articles/12141196-in-platform-survey-types-about-the-monadic-concept-test-survey',
        steps: [
          'Add concept images or names to each quota group when selecting panelists.',
          'Tag relevant questions to dynamically display each group’s assigned concept.',
          'The final report will compare results by concept, based on quota group exposure.',
        ],
        title: 'About the Monadic Concept Test Survey',
      };

    case 'turf':
      return {
        description: `TURF (Total Unduplicated Reach and Frequency) helps identify the optimal combination
        of options—such as features, flavors, or messages—that reaches the largest number of unique respondents.`,
        link: 'https://help.insights.numerator.com/en/articles/12333486-in-platform-survey-types-about-turf',
        steps: [
          'Select your desired panelist criteria.',
          'Add at least one TURF question during questionnaire creation.',
          'The final report will include a TURF analysis showing which combinations maximize reach.',
        ],
        title: 'About the TURF Survey',
      };

    case 'new-item':
      return {
        description: `The New Item Survey helps you capture shopper reactions to newly released products.
        Use it to measure awareness, early perceptions, and purchase intent shortly after launch.`,
        link: 'https://help.numerator.com/en/articles/13455974-in-platform-survey-types-about-the-new-item-survey',
        steps: [
          'Targets items tagged as “New Items” in our system.',
          'Leverage point forward dates to activate continuously fed sample for up to 90 days.',
          'Follow the Help Center guidance to request manual tagging of specific New Items.',
        ],
        title: 'About the New Item Survey',
      };

    case 'sequential-monadic':
      return {
        description: `The Sequential Monadic Concept Test is used to evaluate multiple concepts—such as product designs, claims, or ads—by showing each respondent all concepts one at a time, in a randomized order. This method helps reduce order bias for comparisons across concepts.`,
        link: 'https://help.insights.numerator.com/en/articles/12508219-in-platform-survey-types-about-the-sequential-monadic-concept-test',
        steps: [
          'Create question blocks tied to each concept and mark them for randomization.',
          'Respondents will see each concept’s set of questions in a unique, randomized sequence.',
          'The final report will display results by concept, with indicators showing which question blocks were randomized.',
        ],
        title: 'About the Sequential Monadic Concept Test Survey',
      };

    default:
      return null;
  }
};

/**
 * Determines if a survey group is continuously fed based on its date field configuration.
 * A group is considered continuously fed if it has a DATE field with a single value,
 *
 * @param {Object} group
 * @param {Object[]} group.promptAnswers
 * @param {String} group.promptAnswers[].fieldType
 * @param {Array} group.promptAnswers[].values
 * @returns {Boolean}
 *
 **/
export const isContinuouslyFed = (group) => {
  const dateField = group.promptAnswers.find((answer) => answer.fieldType === 'DATE');
  if (!dateField) return false;
  return dateField.values.length === 1 && typeof dateField.values[0] === 'string';
};

/**
 * Checks if question logic is complete.
 * @param {Object} questionLogic - Question logic object.
 * @returns {boolean} - True if complete, false otherwise.
 */
export const isQuestionLogicComplete = (questionLogic) => {
  if (!questionLogic || Object.keys(questionLogic).length === 0) return true;

  for (const [, logic] of Object.entries(questionLogic)) {
    if (!logic) return false;

    // Disqualify must have at least one answer selected
    if (logic.disqualify && (!logic.disqualify.answers || logic.disqualify.answers.length === 0)) {
      return false;
    }

    const skipLogicRows = logic.skip?.logic;
    if (Array.isArray(skipLogicRows)) {
      for (const row of skipLogicRows) {
        if (!row) return false;

        // Quota group logic
        if (Object.prototype.hasOwnProperty.call(row, 'url_variable')) {
          if (!row.values || row.values.length === 0) return false;
        }
        // Numeric comparison logic
        else if (Object.prototype.hasOwnProperty.call(row, 'comparison_value')) {
          if (!row.question?.id) return false;
          if (row.comparison_value === undefined || row.comparison_value === '') return false;
        }
        // Answer-based logic
        else {
          if (!row.question?.id) return false;
          if (!row.answers || row.answers.length === 0) return false;
        }
      }
    }
  }

  return true;
};
